import { useState, useEffect, useCallback } from 'react';
import {
  loadUserCellPreference,
  saveUserCellPreference,
  type UserCellViewMode,
} from './userCellPreferences';

// Global state for UserCell view mode - shared across all components
let globalViewMode: UserCellViewMode = 'compact';
const listeners = new Set<(mode: UserCellViewMode) => void>();

// Initialize from localStorage
const initViewMode = () => {
  globalViewMode = loadUserCellPreference();
};

// Notify all listeners of view mode change
const notifyListeners = (mode: UserCellViewMode) => {
  listeners.forEach(listener => listener(mode));
};

// Initialize on module load
initViewMode();

/**
 * Hook for managing UserCell view mode with global synchronization
 * All UserCell components share the same view mode preference
 */
export const useUserCellViewMode = () => {
  const [viewMode, setViewModeState] = useState<UserCellViewMode>(() => {
    return globalViewMode;
  });

  // Subscribe to global state changes
  useEffect(() => {
    // Set initial state
    setViewModeState(globalViewMode);

    // Subscribe to changes
    const listener = (mode: UserCellViewMode) => {
      setViewModeState(mode);
    };

    listeners.add(listener);

    // Listen for storage changes from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'user-cell-view-preference' && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          globalViewMode = parsed.viewMode;
          notifyListeners(parsed.viewMode);
        } catch (error) {
          console.error('Failed to parse storage change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      listeners.delete(listener);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Update view mode function
  const setViewMode = useCallback((newMode: UserCellViewMode) => {
    globalViewMode = newMode;
    saveUserCellPreference(newMode);
    notifyListeners(newMode);
  }, []);

  return {
    viewMode,
    setViewMode,
  };
};

